<template>
  <div class="dashboard-container">
    <CCard>
      <CCardHeader style="background-color: #145654;" class="border-bottom px-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <h4 class="card-title mb-0 text-white d-flex align-items-center py-2">
            <CIcon name="cil-speedometer" size="xl" class="mr-2"/> {{ $t('nav.dashboard') }}
          </h4>
          <div class="filter-group d-flex align-items-center flex-wrap py-2">
            <div v-if="selectedRange === 'custom'" class="custom-date-inputs d-flex align-items-center mr-0 mr-sm-3 mb-2 mb-sm-0 bg-white rounded p-1">
              <input
                type="date"
                v-model="startDate"
                class="form-control form-control-sm border-0"
                @change="onCustomDateChange"
              >
              <span class="mx-1 text-dark">-</span>
              <input
                type="date"
                v-model="endDate"
                class="form-control form-control-sm border-0"
                @change="onCustomDateChange"
              >
            </div>
            <CButtonGroup class="mb-2 mb-sm-0">
              <CButton
                v-for="range in rangeOptions"
                :key="range.value"
                :color="selectedRange === range.value ? 'light' : 'outline-light'"
                size="sm"
                @click="selectedRange = range.value"
                :title="range.value === 'custom' ? $t('dashboard.rangeCustom') : range.label"
              >
                <CIcon v-if="range.value === 'custom'" name="cil-filter" />
                <span v-else>{{ range.label }}</span>
              </CButton>
            </CButtonGroup>
          </div>
        </div>
      </CCardHeader>

      <CCardBody class="p-4">
        <CRow>
          <!-- Total Repair Requests -->
          <CCol md="4" sm="6" xs="12" class="mb-4">
            <div class="kpi-card card-total" @click="$router.push('/supervisor/reportlist')" style="cursor: pointer;">
              <div class="icon-wrapper">
                <CIcon name="cil-bell" size="2xl" />
              </div>
              <div class="kpi-content">
                <div class="kpi-value">{{ stats.total }}</div>
                <div class="kpi-label">{{ $t('dashboard.kpiTotalRequests') }}</div>
              </div>
            </div>
          </CCol>

          <!-- In Progress -->
          <CCol md="4" sm="6" xs="12" class="mb-4">
            <div class="kpi-card card-progress">
              <div class="icon-wrapper">
                <CIcon name="cil-av-timer" size="2xl" />
              </div>
              <div class="kpi-content">
                <div class="kpi-value">{{ stats.inProgress }}</div>
                <div class="kpi-label">{{ $t('dashboard.kpiInProgress') }}</div>
              </div>
            </div>
          </CCol>

          <!-- Completed -->
          <CCol md="4" sm="6" xs="12" class="mb-4">
            <div class="kpi-card card-completed">
              <div class="icon-wrapper">
                <CIcon name="cil-check-circle" size="2xl" />
              </div>
              <div class="kpi-content">
                <div class="kpi-value">{{ stats.completed }}</div>
                <div class="kpi-label">{{ $t('dashboard.kpiCompleted') }}</div>
              </div>
            </div>
          </CCol>

          <!-- Overdue SLA -->
          <CCol md="6" sm="6" xs="12" class="mb-4">
            <div class="kpi-card card-overdue">
              <div class="icon-wrapper">
                <CIcon name="cil-clock" size="2xl" />
              </div>
              <div class="kpi-content">
                <div class="kpi-value">{{ stats.overdue }}</div>
                <div class="kpi-label">{{ $t('dashboard.kpiOverSla') }}</div>
              </div>
            </div>
          </CCol>

          <!-- Average Satisfaction -->
          <CCol md="6" sm="6" xs="12" class="mb-4">
            <div class="kpi-card card-satisfaction">
              <div class="icon-wrapper">
                <CIcon name="cil-star" size="2xl" />
              </div>
              <div class="kpi-content">
                <div class="kpi-value">{{ stats.satisfaction }} <span class="text-small">/ 5</span></div>
                <div class="kpi-label">{{ $t('dashboard.kpiSatisfaction') }}</div>
              </div>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CRow>
      <!-- Pie Chart (Categories) -->
      <CCol md="6" class="mb-4">
        <CCard>
          <CCardHeader>
            <h4 class="mb-0">
              <CIcon name="cil-chart-pie" class="mr-2"/> {{ $t('dashboard.pieByCategory') }}
            </h4>
          </CCardHeader>
          <CCardBody>
             <CChartPie :datasets="pieDatasets" :labels="pieLabels" style="height: 400px;"/>
             <div v-if="pieLabels.length === 0" class="text-center mt-3 text-muted">
               <small>{{ $t('dashboard.noData') }}</small>
             </div>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Bar Chart (Monthly) -->
      <CCol md="6" class="mb-4">
        <CCard>
          <CCardHeader>
            <h4 class="mb-0">
              <CIcon name="cil-chart-line" class="mr-2"/> {{ $t('dashboard.barMonthly') }}
            </h4>
          </CCardHeader>
          <CCardBody>
             <CChartBar :datasets="barDatasets" :labels="barLabels" style="height: 400px;"/>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <!-- Detailed Report Section -->
      <CCol md="12" class="mb-4" id="tableColumn">
        <CCard>
          <CCardHeader class="d-flex justify-content-between align-items-center flex-wrap">
            <h4 class="mb-0 py-1">
              <CIcon name="cil-list" class="mr-2"/> {{ $t('dashboard.ticketDetails') }}
            </h4>
            <CButton color="danger" size="sm" class="print-btn my-1" @click="openPdfModal">
              <CIcon name="cil-data-transfer-down" class="mr-1"/> {{ $t('dashboard.reportPdf') }}
            </CButton>
          </CCardHeader>
          <CCardBody style="min-height: 400px; height: 590px; overflow-y: auto;" id="tableCardBody">
            <div class="table-responsive-wrapper">
              <CDataTable
                :items="formattedTickets"
                :fields="reportFields"
                column-filter
                :items-per-page-select="isPaginated ? { values: [5, 10, 20, 50, 100, 500, 1000] } : false"
                :items-per-page.sync="tableItemsPerPage"
                hover
                sorter
                :pagination="isPaginated"
                table-filter
                cleaner
              >
                <template #ticketNo="{item}">
                  <td class="font-weight-bold">
                    {{ item.ticketNo }}
                  </td>
                </template>

                <template #status="{item}">
                  <td>
                    <CBadge :color="getBadge(item._status_raw)">
                      {{ item.status }}
                    </CBadge>
                  </td>
                </template>

                <template #sla="{item}">
                  <td>
                    <CBadge :color="item._slaOverdue ? 'danger' : 'success'">
                      {{ item.sla }}
                    </CBadge>
                    <small class="d-block text-muted d-none d-md-block" v-if="item._sla_hours">{{ $t('dashboard.slaTarget') }} {{ item._sla_hours }}h</small>
                  </td>
                </template>
              </CDataTable>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Hidden PDF Report Template -->
    <div id="pdf-report-container" style="position: absolute; left: -9999px; top: -9999px; width: 1000px; padding: 40px; background: white; font-family: 'Sarabun', sans-serif; color: #333; z-index: -9999;">
      <!-- Header -->
      <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 20px;">
        <div>
          <h1 style="font-size: 28px; font-weight: bold; margin: 0; color: #145654;">{{ $t('dashboard.pdfTitle') }}</h1>
          <div style="color: #666; margin-top: 5px; font-size: 14px;">{{ pdfPeriodText }}</div>
        </div>
        <div style="text-align: right; color: #666; font-size: 14px;">
          {{ $t('dashboard.pdfPrintedAt') }} {{ new Date().toLocaleString(pdfLocale) }}
        </div>
      </div>

      <!-- KPI Section -->
      <div style="display: flex; gap: 15px; margin-bottom: 30px;">
        <div style="flex: 1; background: #fff; border-radius: 12px; padding: 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #eaeaea;">
          <div style="color: #6c757d; font-size: 12px; margin-bottom: 5px;">{{ $t('dashboard.pdfKpiTotal') }}</div>
          <div style="font-size: 24px; font-weight: bold; color: #321fdb;">{{ stats.total }}</div>
        </div>
        <div style="flex: 1; background: #fff; border-radius: 12px; padding: 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #eaeaea;">
          <div style="color: #6c757d; font-size: 12px; margin-bottom: 5px;">{{ $t('dashboard.pdfKpiCompleted') }}</div>
          <div style="font-size: 24px; font-weight: bold; color: #2eb85c;">{{ stats.completed }}</div>
        </div>
        <div style="flex: 1; background: #fff; border-radius: 12px; padding: 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #eaeaea;">
          <div style="color: #6c757d; font-size: 12px; margin-bottom: 5px;">{{ $t('dashboard.pdfKpiInProgress') }}</div>
          <div style="font-size: 24px; font-weight: bold; color: #f9b115;">{{ stats.inProgress }}</div>
        </div>
        <div style="flex: 1; background: #fff; border-radius: 12px; padding: 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #eaeaea;">
          <div style="color: #6c757d; font-size: 12px; margin-bottom: 5px;">{{ $t('dashboard.pdfKpiOverdue') }}</div>
          <div style="font-size: 24px; font-weight: bold; color: #e55353;">{{ stats.overdue }}</div>
        </div>
        <div style="flex: 1; background: #fff; border-radius: 12px; padding: 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #eaeaea;">
          <div style="color: #6c757d; font-size: 12px; margin-bottom: 5px;">{{ $t('dashboard.pdfKpiSatisfaction') }}</div>
          <div style="font-size: 24px; font-weight: bold; color: #f6993f;">{{ stats.satisfaction }} / 5</div>
        </div>
      </div>

      <!-- Report Details Section (Middle) -->
      <div style="margin-bottom: 30px; background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #eaeaea;">
        <h3 style="font-size: 18px; font-weight: bold; margin-bottom: 15px; color: #333; margin-top: 0;">{{ $t('dashboard.pdfWorkListTitle') }}</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          <thead>
            <tr style="background-color: #f8f9fa; border-bottom: 2px solid #dee2e6;">
              <th style="padding: 10px; text-align: left;">{{ $t('dashboard.colTicketNo') }}</th>
              <th style="padding: 10px; text-align: left;">{{ $t('dashboard.colDateReported') }}</th>
              <th style="padding: 10px; text-align: left;">{{ $t('dashboard.colReporterDept') }}</th>
              <th style="padding: 10px; text-align: left;">{{ $t('dashboard.colStatus') }}</th>
              <th style="padding: 10px; text-align: left;">{{ $t('dashboard.colSla') }}</th>
              <th style="padding: 10px; text-align: left;">{{ $t('dashboard.colClosedAt') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in formattedTickets" :key="index" style="border-bottom: 1px solid #e9ecef;">
              <td style="padding: 10px; font-weight: bold;">{{ item.ticketNo }}</td>
              <td style="padding: 10px;">{{ item.dateInformed }}</td>
              <td style="padding: 10px;">{{ item.reporter }}</td>
              <td style="padding: 10px;">
                <span :style="{ color: pdfStatusTextColor(item._status_raw) }">
                  {{ item.status }}
                </span>
              </td>
              <td style="padding: 10px;">
                <span :style="{ color: item._slaOverdue ? '#e55353' : '#2eb85c' }">{{ item.sla }}</span>
              </td>
              <td style="padding: 10px;">{{ item.closedDate }}</td>
            </tr>
            <tr v-if="formattedTickets.length === 0">
              <td colspan="6" style="padding: 20px; text-align: center; color: #6c757d;">{{ $t('dashboard.noRowsInPeriod') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Table Summary -->
      <div style="background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #eaeaea; page-break-inside: avoid;">
        <h3 style="font-size: 18px; font-weight: bold; margin-bottom: 15px; color: #333; margin-top: 0;">{{ $t('dashboard.summaryTitle') }}</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          <thead>
            <tr style="background-color: #f8f9fa; border-bottom: 2px solid #dee2e6;">
              <th style="padding: 10px; text-align: left;">{{ $t('dashboard.summaryColCategory') }}</th>
              <th style="padding: 10px; text-align: center;">{{ $t('dashboard.summaryColTotal') }}</th>
              <th style="padding: 10px; text-align: center;">{{ $t('dashboard.summaryColCompleted') }}</th>
              <th style="padding: 10px; text-align: center;">{{ $t('dashboard.summaryColInProgress') }}</th>
              <th style="padding: 10px; text-align: center;">{{ $t('dashboard.summaryColOverdue') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(summ, idx) in summaryData" :key="idx" style="border-bottom: 1px solid #e9ecef;">
              <td style="padding: 10px;">{{ summ.name }}</td>
              <td style="padding: 10px; text-align: center;">{{ summ.total }}</td>
              <td style="padding: 10px; text-align: center; color: #2eb85c;">{{ summ.completed }}</td>
              <td style="padding: 10px; text-align: center; color: #f9b115;">{{ summ.inProgress }}</td>
              <td style="padding: 10px; text-align: center; color: #e55353;">{{ summ.overdue }}</td>
            </tr>
            <tr v-if="summaryData.length === 0">
              <td colspan="5" style="padding: 20px; text-align: center; color: #6c757d;">{{ $t('dashboard.noSummaryRows') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <CModal
      :title="$t('dashboard.pdfModalTitle')"
      color="info"
      :show.sync="showPdfModal"
    >
      <div class="form-group">
        <label>{{ $t('dashboard.dateFrom') }}</label>
        <input type="date" class="form-control" v-model="pdfStartDate">
      </div>
      <div class="form-group">
        <label>{{ $t('dashboard.dateTo') }}</label>
        <input type="date" class="form-control" v-model="pdfEndDate">
      </div>
      <template #footer>
        <CButton color="secondary" @click="cancelPdfModal">{{ $t('sendReport.cancel') }}</CButton>
        <CButton color="primary" @click="confirmDownloadPdf" :disabled="!pdfStartDate || !pdfEndDate">{{ $t('dashboard.download') }}</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { CChartPie, CChartBar } from '@coreui/vue-chartjs'

export default {
  name: 'SupervisorDashboard',
  components: {
    CChartPie,
    CChartBar
  },
  data() {
    return {
      selectedRange: 'today',
      startDate: '',
      endDate: '',
      tickets: [],
      categories: [],
      stats: {
        total: 0,
        inProgress: 0,
        completed: 0,
        overdue: 0,
        satisfaction: 0
      },
      tableItemsPerPage: 5,
      isPaginated: true,
      showPdfModal: false,
      pdfStartDate: '',
      pdfEndDate: '',
      prevItemsPerPageBeforePdf: null
    }
  },
  computed: {
    pdfLocale() {
      void this.$store.state.locale.lang
      return this.$store.state.locale.lang === 'en' ? 'en-GB' : 'th-TH'
    },
    rangeOptions() {
      void this.$store.state.locale.lang
      return [
        { value: 'custom' },
        { label: this.$t('dashboard.rangeToday'), value: 'today' },
        { label: this.$t('dashboard.rangeWeek'), value: 'week' },
        { label: this.$t('dashboard.rangeMonth'), value: 'month' },
        { label: this.$t('dashboard.rangeYear'), value: 'year' },
        { label: this.$t('dashboard.rangeAll'), value: 'all' }
      ]
    },
    reportFields() {
      void this.$store.state.locale.lang
      return [
        { key: 'ticketNo', label: this.$t('dashboard.field.ticketNo'), _classes: 'font-weight-bold', _style: 'width: 100px;' },
        { key: 'dateInformed', label: this.$t('dashboard.field.dateInformed'), _style: 'min-width: 120px;' },
        { key: 'reporter', label: this.$t('dashboard.field.reporter'), _classes: 'd-none d-lg-table-cell' },
        { key: 'category', label: this.$t('dashboard.field.category'), _classes: 'd-none d-md-table-cell' },
        { key: 'status', label: this.$t('dashboard.field.status') },
        { key: 'sla', label: this.$t('dashboard.field.sla'), _classes: 'd-none d-sm-table-cell' },
        { key: 'assignee', label: this.$t('dashboard.field.assignee'), _classes: 'd-none d-xl-table-cell' },
        { key: 'closedDate', label: this.$t('dashboard.field.closedDate'), _style: 'min-width: 120px;', _classes: 'd-none d-lg-table-cell' }
      ]
    },
    dateFilteredTickets() {
      const now = moment()
      let filtered = this.tickets.filter(t => t.status !== 'cancelled')

      return filtered.filter(t => {
        const date = moment(t.userPart?.dateTimeInformed || t.createdAt)
        switch(this.selectedRange) {
          case 'today': return date.isSame(now, 'day')
          case 'week': return date.isSame(now, 'week')
          case 'month': return date.isSame(now, 'month')
          case 'year': return date.isSame(now, 'year')
          case 'all': return true
          case 'custom':
             if (this.startDate && this.endDate) {
                 return date.isBetween(moment(this.startDate).startOf('day'), moment(this.endDate).endOf('day'), null, '[]')
             }
             return true
          default: return true
        }
      })
    },
    formattedTickets() {
      const flattenCategories = []
      this.categories.forEach(cat => {
        if(cat.subCategories && Array.isArray(cat.subCategories)) {
          cat.subCategories.forEach(sub => {
             flattenCategories.push({
               dep: cat.departmentName,
               name: sub.name,
               sla: sub.slaHours
             })
          })
        }
      })

      return this.dateFilteredTickets.map(t => {
        let slaHours = 24
        const subName = t.userPart?.subCategory?.name || t.userPart?.subCategory
        const matched = flattenCategories.find(s => s.name === subName)
        if (matched) slaHours = matched.sla

        const startTime = moment(t.userPart?.dateTimeInformed || t.createdAt)
        const deadline = startTime.clone().add(slaHours, 'hours')

        let endTime = moment()
        const isClosed = ['done', 'welldone'].includes(t.status)
        if (isClosed) {
           endTime = moment(t.updatedAt)
        }

        const isOverdue = endTime.isAfter(deadline)

        let assignee = '-'
        const assignedTech = t.supervisorPart?.assignedTechnician
        const supervisorObj = t.supervisorPart?.supervisor

        if (assignedTech && typeof assignedTech === 'object') {
            assignee = `${assignedTech.name || ''} ${assignedTech.surname || ''}`.trim()
        } else if (supervisorObj && typeof supervisorObj === 'object') {
             assignee = this.$t('dashboard.supervisorPrefix') + `${supervisorObj.name || ''} ${supervisorObj.surname || ''}`.trim()
        }

        const statusLabel = this.getStatusLabel(t.status)
        const slaLabel = isOverdue ? this.$t('dashboard.slaOverdue') : this.$t('dashboard.slaOnTime')

        const displayId = t.ticketNumber || (t.id || t._id || '').substring(0, 8).toUpperCase() || 'N/A'

        const reporterName = t.userPart?.user
          ? `${t.userPart.user.name || ''} ${t.userPart.user.surname || ''}`.trim()
          : (t.userPart?.fullname || t.userPart?.username || '-')

        const reporterDept = t.userPart?.category?.departmentName || t.userPart?.department || ''

        return {
          ticketNo: displayId,
          dateInformed: this.formatDate(t.userPart?.dateTimeInformed || t.createdAt),
          reporter: reporterDept ? `${reporterName} / ${reporterDept}` : reporterName,
          category: subName || '-',
          status: statusLabel,
          sla: slaLabel,
          assignee: assignee,
          closedDate: isClosed ? this.formatDate(t.updatedAt) : '-',

          _status_raw: t.status,
          _sla_hours: slaHours,
          _slaOverdue: isOverdue
        }
      })
    },
    pdfPeriodText() {
        void this.$store.state.locale.lang
        const loc = this.pdfLocale
        if (this.startDate && this.endDate && this.selectedRange === 'custom') {
            const dStart = new Date(this.startDate).toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' })
            const dEnd = new Date(this.endDate).toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' })
            return this.$t('dashboard.pdfPeriodTpl').replace('{from}', dStart).replace('{to}', dEnd)
        }
        const opt = this.rangeOptions.find(r => r.value === this.selectedRange)
        const lbl = opt && opt.label ? opt.label : this.$t('dashboard.rangeAll')
        return this.$t('dashboard.periodPrefix') + lbl
    },
    summaryData() {
      const summary = {};
      const flattenCategories = [];
      this.categories.forEach(cat => {
        if(cat.subCategories && Array.isArray(cat.subCategories)) {
          cat.subCategories.forEach(sub => {
             flattenCategories.push({
               dep: cat.departmentName,
               name: sub.name,
               sla: sub.slaHours
             })
          })
        }
      });
      
      this.dateFilteredTickets.forEach(t => {
        const subName = t.userPart?.subCategory?.name || t.userPart?.subCategory || this.$t('common.other');
        if (!summary[subName]) {
          summary[subName] = { name: subName, total: 0, completed: 0, inProgress: 0, overdue: 0 };
        }
        summary[subName].total++;
        
        const isClosed = ['done', 'welldone'].includes(t.status);
        if (isClosed) summary[subName].completed++;
        else if (['pending', 'assigned', 'in_progress'].includes(t.status)) summary[subName].inProgress++;

        let slaHours = 24;
        const matched = flattenCategories.find(s => s.name === subName);
        if (matched) slaHours = matched.sla;

        const startTime = moment(t.userPart?.dateTimeInformed || t.createdAt);
        const deadline = startTime.clone().add(slaHours, 'hours');
        let endTime = moment();
        if (isClosed) {
           endTime = moment(t.updatedAt);
        }

        if (endTime.isAfter(deadline)) {
          summary[subName].overdue++;
        }
      });
      return Object.values(summary).sort((a,b) => b.total - a.total);
    },
    pieLabels() {
      return this.summaryData.map(s => s.name);
    },
    pieDatasets() {
      const data = this.summaryData.map(s => s.total);
      const backgroundColors = [
        '#41B883', '#E46651', '#00D8FF', '#DD1B16', 
        '#f9b115', '#321fdb', '#e55353', '#ced2d8',
        '#2eb85c', '#3399ff', '#f6993f', '#6f42c1'
      ];
      return [
        {
          backgroundColor: backgroundColors.slice(0, data.length),
          data: data
        }
      ]
    },
    barLabels() {
       void this.$store.state.locale.lang
       return Array.from({ length: 12 }, (_, i) => this.$t(`dashboard.shortMonth${i + 1}`))
    },
    barDatasets() {
       const catCounts = {};
       
       this.dateFilteredTickets.forEach(t => {
          const subName = t.userPart?.subCategory?.name || t.userPart?.subCategory || this.$t('common.other');
          if (!catCounts[subName]) {
             catCounts[subName] = new Array(12).fill(0);
          }
          const date = moment(t.userPart?.dateTimeInformed || t.createdAt);
          catCounts[subName][date.month()]++;
       });

       const backgroundColors = [
        '#41B883', '#E46651', '#00D8FF', '#DD1B16', 
        '#f9b115', '#321fdb', '#e55353', '#ced2d8',
        '#2eb85c', '#3399ff', '#f6993f', '#6f42c1'
       ];

       return Object.keys(catCounts).map((catName, index) => {
         return {
           label: catName,
           backgroundColor: backgroundColors[index % backgroundColors.length],
           data: catCounts[catName]
         };
       });
    }
  },
  watch: {
    selectedRange() {
      this.calculateStats()
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        const user = JSON.parse(localStorage.getItem('user'))
        const departmentId = user ? user.department : ''

        if (!departmentId) {
          console.warn("No departmentId found in localStorage for supervisor")
          return
        }

        const [ticketsRes, categoriesRes] = await Promise.all([
          axios.get(`${process.env.VUE_APP_APP_URL}/ticket/supervisor/${departmentId}/all`),
          axios.get(`${process.env.VUE_APP_APP_URL}/category`)
        ])

        if (ticketsRes.data.success) {
          this.tickets = ticketsRes.data.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        }
        if (categoriesRes.data.success) {
          this.categories = categoriesRes.data.data
        }

        this.calculateStats()

      } catch (error) {
        console.error('Error fetching supervisor dashboard data:', error)
      }
    },
    calculateStats() {
      const filtered = this.dateFilteredTickets

      const total = filtered.length
      const inProgress = filtered.filter(t => ['pending', 'assigned', 'in_progress'].includes(t.status)).length
      const completed = filtered.filter(t => ['done', 'welldone'].includes(t.status)).length

      let overdue = 0

      const flattenCategories = []
      this.categories.forEach(cat => {
        if(cat.subCategories && Array.isArray(cat.subCategories)) {
          cat.subCategories.forEach(sub => {
             flattenCategories.push({
               dep: cat.departmentName,
               name: sub.name,
               sla: sub.slaHours
             })
          })
        }
      })

      filtered.forEach(t => {
        let slaHours = 24
        const subName = t.userPart?.subCategory?.name || t.userPart?.subCategory

        const matched = flattenCategories.find(s => s.name === subName)
        if (matched) slaHours = matched.sla

        const startTime = moment(t.userPart?.dateTimeInformed || t.createdAt)
        const deadline = startTime.clone().add(slaHours, 'hours')

        let endTime = moment()
        if (['done', 'welldone'].includes(t.status)) {
           endTime = moment(t.updatedAt)
        }

        if (endTime.isAfter(deadline)) {
          overdue++
        }
      })

      const ratedTickets = filtered.filter(t => t.rating && t.rating.stars > 0)
      let avgRating = 0
      if (ratedTickets.length > 0) {
        const sum = ratedTickets.reduce((acc, t) => acc + t.rating.stars, 0)
        avgRating = (sum / ratedTickets.length).toFixed(1)
      }

      this.stats = {
        total,
        inProgress,
        completed,
        overdue,
        satisfaction: avgRating
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return moment(date).format('DD/MM/YYYY HH:mm')
    },
    getBadge(status) {
      switch (status) {
        case 'pending': return 'warning'
        case 'assigned': return 'info'
        case 'in_progress': return 'primary'
        case 'done': return 'success'
        case 'welldone': return 'success'
        case 'cancelled': return 'danger'
        case 'rejected': return 'danger'
        default: return 'secondary'
      }
    },
    getStatusLabel(status) {
        const key = `dashboard.status.${status}`
        const translated = this.$t(key)
        return translated !== key ? translated : status
    },
    pdfStatusTextColor(statusRaw) {
      if (['done', 'welldone'].includes(statusRaw)) return '#2eb85c'
      if (['cancelled', 'rejected'].includes(statusRaw)) return '#e55353'
      if (['in_progress', 'assigned'].includes(statusRaw)) return '#321fdb'
      return '#f9b115'
    },
    async loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve()
        const script = document.createElement('script')
        script.src = src
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    },
    openPdfModal() {
      this.showPdfModal = true
    },
    cancelPdfModal() {
      this.showPdfModal = false
    },
    async confirmDownloadPdf() {
      if (!this.pdfStartDate || !this.pdfEndDate) return
      
      this.showPdfModal = false

      const oldRange = this.selectedRange
      const oldStartDate = this.startDate
      const oldEndDate = this.endDate

      this.selectedRange = 'custom'
      this.startDate = this.pdfStartDate
      this.endDate = this.pdfEndDate

      await this.$nextTick()
      await new Promise(r => setTimeout(r, 600)) // delay for render

      await this.downloadPdf()

      this.selectedRange = oldRange
      this.startDate = oldStartDate
      this.endDate = oldEndDate
      
      this.$nextTick(() => {
        this.calculateStats()
      })
    },
    async downloadPdf() {
      try {
        await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
        await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')

        const element = document.getElementById('pdf-report-container')
        if (!element) return

        element.style.left = '0'
        element.style.top = '0'
        
        await this.$nextTick()
        await new Promise(r => setTimeout(r, 800)) // delay for render

        const canvas = await window.html2canvas(element, {
          scale: 1.5,
          useCORS: true,
          backgroundColor: '#f3f4f6',
          width: 1000,
          windowWidth: 1000
        })

        element.style.left = '-9999px'
        element.style.top = '-9999px'

        const { jsPDF } = window.jspdf
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true })

        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()
        const imgWidth = pageWidth - 20
        const imgHeight = (canvas.height * imgWidth) / canvas.width
        const imgData = canvas.toDataURL('image/jpeg', 0.8)
        
        let y = 10
        let sourceY = 0

        if (imgHeight + y <= pageHeight) {
          pdf.addImage(imgData, 'JPEG', 10, y, imgWidth, imgHeight)
        } else {
          pdf.addImage(imgData, 'JPEG', 10, y, imgWidth, imgHeight)
          sourceY += (pageHeight - y)

          while (sourceY < imgHeight) {
            pdf.addPage()
            
            const position = 10 - sourceY
            pdf.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight)
            
            pdf.setFillColor(255, 255, 255)
            pdf.rect(0, 0, pageWidth, 10, 'F')
            
            sourceY += (pageHeight - 10)
          }
        }

        const dateStr = new Date().toISOString().slice(0, 10)
        pdf.save(`ticket-report-${dateStr}.pdf`)
      } catch (err) {
        console.error('PDF generation failed:', err)
      }
    },

    onCustomDateChange() {
      if (this.selectedRange === 'custom' && this.startDate && this.endDate) {
        this.calculateStats()
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;700&display=swap');

.kpi-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;
  height: 100px;
  border-left: 5px solid transparent;
}

.kpi-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.kpi-content {
  flex-grow: 1;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.kpi-label {
  font-size: 14px;
  color: #6c757d;
}

.text-small {
  font-size: 14px;
  font-weight: 400;
  color: #adb5bd;
}

/* Specific Card Styles */
.card-total {
  border-left-color: #321fdb;
}
.card-total .icon-wrapper {
  background-color: rgba(50, 31, 219, 0.1);
  color: #321fdb;
}

.card-progress {
  border-left-color: #f9b115;
}
.card-progress .icon-wrapper {
  background-color: rgba(249, 177, 21, 0.1);
  color: #f9b115;
}

.card-completed {
  border-left-color: #2eb85c;
}
.card-completed .icon-wrapper {
  background-color: rgba(46, 184, 92, 0.1);
  color: #2eb85c;
}

.card-overdue {
  border-left-color: #e55353;
}
.card-overdue .icon-wrapper {
  background-color: rgba(229, 83, 83, 0.1);
  color: #e55353;
}

.card-satisfaction {
  border-left-color: #f9b115; /* Star color */
}
.card-satisfaction .icon-wrapper {
  background-color: rgba(249, 177, 21, 0.1);
  color: #f9b115;
}

/* Dark/Gradient Header override if needed */
.card-header .btn-outline-light:hover {
  color: #145654;
}

@media print {
  /* Hide common layout components */
  .c-sidebar, .c-header, .c-footer {
    display: none !important;
  }
  .c-wrapper {
    margin-left: 0 !important;
  }
  .c-main, .c-app {
    background: white !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  /* Hide everything except the ticket detail section */
  .dashboard-container > .card:first-child {
    display: none !important;
  }
  
  #tableColumn {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  /* Hide filter and print button from print layout */
  .filter-group,
  .print-btn {
    display: none !important;
  }

  /* Hide CDataTable filter bar and items-per-page selector */
  .table-responsive + div,
  .c-datatable-filter,
  .datatable-filter,
  [class*="datatable"] .form-inline,
  .form-inline.float-right,
  .col-sm-6.form-inline {
    display: none !important;
  }

  /* For card UI to look good in PDF */
  .card {
    border: none !important;
    box-shadow: none !important;
  }
  
  .card-header {
    background-color: transparent !important;
    border-bottom: 2px solid #000 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .kpi-card {
    display: none !important;
  }

  /* Keep background colors */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Fix scrolling on card body */
  .card-body {
    height: auto !important;
    overflow-y: visible !important;
  }
}

/* --- Mobile Responsive Enhancements --- */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 0;
  }
  
  .kpi-value {
    font-size: 1.5rem !important;
  }
  
  .kpi-label {
    font-size: 0.8rem !important;
  }
  
  .icon-wrapper {
    width: 45px !important;
    height: 45px !important;
  }
  
  .card-body {
    padding: 1rem !important;
  }

  /* Reduce charts height on mobile to prevent excessive scrolling */
  canvas {
    max-height: 250px !important;
  }
  
  /* Custom date inputs stacking */
  .custom-date-inputs {
    width: 100%;
    justify-content: space-between;
  }
  .custom-date-inputs input {
    width: 45% !important;
    font-size: 11px;
    padding: 2px 5px;
  }
}

@media (max-width: 480px) {
  .card-title {
    font-size: 1.1rem;
  }
  
  .filter-group .btn {
    padding: 0.25rem 0.4rem;
    font-size: 0.7rem;
  }
}
</style>
